export enum NotificationFlags {
  METER_HEALTH_SUMMARY = 'METER_HEALTH_SUMMARY',
  // INSTALL_HEALTH_SUMMARY = 'INSTALL_HEALTH_SUMMARY',
  LEAK = 'LEAK',
  HIGH_USAGE = 'HIGH_USAGE',
  // MESSAGES_SEEN_EVENT = 'MESSAGES_SEEN_EVENT',
  // METER_MODE_ACHIEVED_EVENT = 'METER_MODE_ACHIEVED_EVENT',
  INITIAL_METER_HEALTHY_EVENT = 'INITIAL_METER_HEALTHY_EVENT',
  METER_UNHEALTHY_EVENT = 'METER_UNHEALTHY_EVENT',
  METER_HEALTHY_EVENT = 'METER_HEALTHY_EVENT',
}

export interface IUserData {
  orgName: string;
  connectedAccountId: string;
  connectedAccountDetailsSubmitted: boolean;
  serviceFeeBillingMethod: number; // 0 = EXTERNAL, 1 = STRIPE
}

export interface IAccontInfo {
  user: IUser;
}

export interface IFeatures {
  features: string[];
}

interface IUser {
  id: string;
  email: string;
  note: string;
  organization_name: string;
  phone_number: string;
  stripe_connected_account_id: string;
  stripe_connected_account_details_submitted: boolean;
}

export interface IBodyUpdateUserInfo {
  organization_name: string;
}

export interface IBodyCreateAccountLink {
  link_type: STRIPE_ACCOUNT_LINK_TYPE;
}

export enum STRIPE_ACCOUNT_LINK_TYPE {
  ACCOUNT_ONBOARDING = 'ACCOUNT_ONBOARDING',
}

export interface ICreateAccountLinkResponse {
  url: string;
}

/* ---- New types for multi-user account access ---- */

/**
 * Represents a single user account on your main account
 * Now includes both notification settings and summary schedule settings
 */
export interface IUserAccountBase {
  id: string;
  user_id: string;
  email: string;
  phonenumber: string | null;
  region: string;
  note: string | null;
  user_status: string;
  created: number;
  last_updated: number;
  last_signed_in: number | null;
  email_bounce_alerted: boolean;
  enabled_email_notifications: NotificationFlags[];
  enabled_sms_notifications: NotificationFlags[];
  timezone_key: string;
  meter_health_summary_enabled: boolean;
  meter_health_summary_day_of_week: number;
  meter_health_summary_hour: number;
  role_key?: string; // RBAC role: owner, admin, billing_manager
}

/**
 * Request payload for updating a UserAccount
 */
export type UserAccountUpdateRequest = {
  note: string;
  phone_number?: string | null;
  enabled_email_notifications: string[];
  enabled_sms_notifications: string[];
};

/**
 * Response from PUT /userAccount/{id}
 */
export type IUpdateUserAccountNoteResponse = IUserAccountBase;

/**
 * The Account record including all associated user accounts
 */
export interface IAccountWithUserAccounts {
  id: string;
  created: number;
  note: string | null;
  organization_name: string;
  stripe_connected_account_id: string;
  stripe_connected_account_details_submitted: boolean;
  associated_user_accounts: IUserAccountBase[];
  timezone_key: string;
  meter_health_summary_enabled: boolean;
  meter_health_summary_day_of_week: number;
  meter_health_summary_hour: number;
  service_fee_billing_method: number; // 0 = EXTERNAL, 1 = STRIPE
  pms_type: string | null;
  pms_config: { company_code?: string } | null;
}

/**
 * Body payload for inviting a new user account
 */
export interface IAddUserAccountRequest {
  email: string;
  phone_number?: string | null;
  note: string | null;
  invite_memo?: string | null;
  role_key: string;
}

/**
 * Response from inviting a new user (same as full account record)
 */
export type IAddUserAccountResponse = IAccountWithUserAccounts;

/**
 * Body payload for updating an existing user account
 */
export interface IUpdateUserAccountRequest {
  email: string;
  user_id: string;
  invite_memo?: string | null;
}

/**
 * Response from updating a user account (same as full account record)
 */
export type IUpdateUserAccountResponse = IAccountWithUserAccounts;

/**
 * Request payload for updating account-level summary settings
 */
export interface IUpdateAccountRequest {
  timezone_key?: string;
  meter_health_summary_enabled?: boolean;
  meter_health_summary_day_of_week?: number;
  meter_health_summary_hour?: number;
}

/**
 * Response from updating account settings (same as full account record)
 */
export type IUpdateAccountResponse = IAccountWithUserAccounts;

/**
 * Role definition from backend
 */
export interface IRole {
  role_key: string;
  role_name: string;
  description: string;
  user_permissions: number;
}

/**
 * Response from GET /v1/roles
 */
export interface IListRolesResponse {
  roles: IRole[];
}

/**
 * Response from GET /v1/me/permissions
 */
export interface IMyPermissionsResponse {
  permissions: number;
  role_key: string | null;
  account_id: string;
}

/**
 * Request payload for PATCH /v1/account/members/{userId}/role
 */
export interface ISetMemberRoleRequest {
  role_key: string;
}
